import { useEffect, useRef, useState } from "react";
import { themeVar as colors } from "../design-tokens";
import { stageLabel } from "./constants";
import type { AmbJobPublic } from "./types";

/**
 * The build screen's progress panel.
 *
 * THE COMPLAINT THIS ANSWERS: "we don't know if anything is happening when the report is being
 * built." The screen showed a single stage label on a static card. A full AMB is one long Opus call
 * plus a craft redraft plus an editor pass plus a QA/figure audit, and for most of that the label
 * did not change — so an agent watching it had no way to tell a working build from a hung one.
 *
 * WHAT THIS PANEL DOES NOT COVER (corrected 2026-08-07): PDF and Word rendering. Those happen on
 * the download route, on the user's click, AFTER the job reaches `done`, with no progress UI at
 * all. This header and the note near the end of the bar both used to say the build was "building
 * the PDF and Word files", which described work the job provably does not do — the fastest way to
 * lose a reader is to narrate a step they can later prove was not running. If the PDF wait needs
 * setting up, it belongs on the download action, not on this panel.
 *
 * A percentage was impossible until now: the server's `progress` is a stage index across two
 * disjoint job types, so a finished extract reported 50%. `progress_pct` is the real one.
 *
 * THE HONESTY RULE for the rotating lines below. They are keyed to the CURRENT SERVER STAGE, so
 * every line describes work that is genuinely happening right now. A generic carousel of
 * reassurances that runs regardless of state is a progress bar that lies slowly, and an agent who
 * catches it once stops believing the rest of the screen.
 */

/** What each stage is actually doing, in the words a preparer would use. Rotated one at a time.
 *
 * `compute+draft` is deliberately NOT split into sub-stages even though it is ~95% of the clock.
 * The worker does not report where it is inside that stage — the draft, the craft redraft and the
 * editor pass are three sequential model calls behind one `_job_set`. Inventing sub-stage keys the
 * server never sends would put a label on screen that no code can be wrong about, which is the same
 * class of lie as the PDF note this file just removed. The rotation below is honest because every
 * line is true for the whole stage; the TIMING is carried by the bar, not by the label. */
const STAGE_ACTIVITY: Record<string, string[]> = {
  queued: ["Your report is in the queue and will start in a moment"],
  read: ["Opening your files", "Reading the pages in order"],
  ocr: [
    "This file is scanned, so every page is being read one at a time",
    "Scanned pages take the longest — the text has to be recognized before it can be used",
  ],
  ai_extract: [
    "Transcribing the subject and each comparable",
    "Nothing is estimated — only what is printed in your file is taken",
  ],
  extract: ["Reading your evidence"],
  "compute+draft": [
    "Adjusting every comparable to your home, line by line",
    "Weighting the closest sales most heavily — the ones that needed the fewest adjustments",
    "Reconciling the adjusted figures into a single estimate",
    "Writing the brief in the voice you chose",
    "Every dollar figure is computed before a word is written, so none of them can drift",
  ],
  qa: [
    "Checking every figure in the prose against its source",
    "Confirming no number appears that the evidence does not support",
  ],
  done: ["Finishing up"],
};

const FALLBACK_ACTIVITY = ["Working on your report"];
const ROTATE_MS = 5000;

/** The job as this panel reads it, plus the stage-timing fields the server added on 2026-08-07.
 *
 * Declared here rather than in `types.ts` on purpose: they are additive and optional, so an
 * `AmbJobPublic` from any other caller still satisfies this, and a server that has not shipped them
 * yet simply leaves them undefined and gets the old fixed-half-life easing below. Fold them into
 * `AmbJobPublic` when types.ts is next touched. */
export type BuildProgressJob = AmbJobPublic & {
  /** ISO-8601 (UTC, explicit offset) instant the job entered its CURRENT stage. Informational —
   * the pacing below uses `stage_elapsed_sec`, which cannot be wrong about clock skew. */
  stage_started_at?: string | null;
  /** Seconds the job has already spent in this stage, measured on the SERVER. */
  stage_elapsed_sec?: number | null;
  /** How long this stage usually takes. Not a deadline: see `stageEase`. */
  stage_expected_sec?: number | null;
};

/** Fraction of the current stage's band the bar may show, given how long the stage has run.
 *
 * WHY THIS SHAPE (2026-08-07, owner: "Its still quick to get to 30% ... we know that these take
 * about 2 minutes and 15 seconds ... 10 seconds in its not at 30%"). The previous easer was
 * `1 - 0.5^(elapsed/120)`: a fixed half-life with no knowledge of the job's real duration. It rose
 * fastest at the very start — ~6% of the band in the first 10 seconds ON TOP of a band floor of 30
 * — so the fastest movement happened in the window where the least work was done.
 *
 * This is a smoothstep (3r^2 - 2r^3) over r = elapsed/expected, scaled to 90% of the band, with an
 * asymptotic tail past r = 1 that closes half the remaining sliver every half-expected. Smoothstep
 * because the complaint is specifically about the FIRST ten seconds: it starts nearly flat, so a
 * job 8% into its expected duration reads ~2% of the band rather than the ~6% a decaying
 * exponential would give it, and it does its moving in the middle where the work actually is.
 *
 * On a 125s `compute+draft` band of 6..92 that is: 10s -> 7%, 60s -> 42%, 135s -> 84%, 200s -> 88%,
 * and it can never pass 91 (ceiling - 1). Reaching the expected duration is ~90% of the band, never
 * the end of it: overrunning is normal and must not look like completion. */
export function stageEase(elapsedSec: number, expectedSec: number): number {
  if (!(expectedSec > 0)) return 0;
  const r = Math.max(elapsedSec, 0) / expectedSec;
  if (r >= 1) return 1 - 0.1 * Math.pow(0.5, (r - 1) / 0.5);
  return 0.9 * (3 * r * r - 2 * r * r * r);
}

export function BuildProgress({
  job,
  notice,
  stalled,
}: {
  job: BuildProgressJob;
  notice?: string | null;
  stalled?: boolean;
}) {
  const [elapsed, setElapsed] = useState(0);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), ROTATE_MS);
    return () => clearInterval(t);
  }, []);

  const serverPct = typeof job.progress_pct === "number" ? job.progress_pct : null;
  const ceiling = typeof job.progress_ceiling_pct === "number" ? job.progress_ceiling_pct : null;

  const expectedSec =
    typeof job.stage_expected_sec === "number" && job.stage_expected_sec > 0
      ? job.stage_expected_sec
      : null;
  const serverElapsed =
    typeof job.stage_elapsed_sec === "number" && job.stage_elapsed_sec >= 0
      ? job.stage_elapsed_sec
      : null;

  /* EASE THROUGH THE STAGE, NEVER PAST IT (2026-08-04, owner: "The building report goes
     immediately to 30% and hangs, that can give the illusion that it is stuck"; refined 2026-08-07,
     owner: "Its still quick to get to 30% ... this timing should be built into the progression bar").

     `compute+draft` is the model call — ~95% of a build's clock — and there is no counter inside it
     to interpolate on, the way OCR interpolates on pages. So rather than invent one, the bar creeps
     from the stage's own figure toward the CEILING the server sends for that stage, approaching it
     asymptotically and never arriving. Every number shown is still bounded by the stage the job is
     genuinely in; the bar can only enter the next band when the stage actually changes, and it
     never moves backwards, so a poll that re-reports the same stage cannot make it stutter.

     What changed on 2026-08-07 is the PACE, not the bound. The first version crept on a fixed
     2-minute half-life that knew nothing about how long the stage should take, and the server's
     band floor was 30, so ten seconds into a 135-second job the bar read 30-something. Now the
     server publishes the stage's expected duration and the server-measured elapsed, and the curve
     is driven by elapsed-vs-expected (see `stageEase`).

     This remains a display affordance and deliberately not precision: it says "still working, and
     not yet finished with this step", which is exactly what is true. */
  const stageClock = useRef<{ stage: string; base: number; at: number }>({
    stage: job.stage,
    base: serverElapsed ?? 0,
    at: Date.now(),
  });
  const shownRef = useRef<number>(serverPct ?? 0);
  if (stageClock.current.stage !== job.stage) {
    stageClock.current = { stage: job.stage, base: serverElapsed ?? 0, at: Date.now() };
  } else if (serverElapsed != null && serverElapsed !== stageClock.current.base) {
    // Re-anchor on every poll that carries a fresh server-side age. Polls are 2s apart and the bar
    // re-renders every 1s, so between polls we add local time to the last server number. Anchoring
    // on the SERVER's count (not on when this component mounted) is what stops a browser refresh
    // mid-build from restarting the ease at the band floor in front of the user.
    stageClock.current = { stage: job.stage, base: serverElapsed, at: Date.now() };
  }
  const stageElapsed = stageClock.current.base + (Date.now() - stageClock.current.at) / 1000;

  let pct = serverPct;
  if (serverPct != null && ceiling != null && ceiling > serverPct) {
    const eased =
      expectedSec != null
        ? stageEase(stageElapsed, expectedSec)
        // No expected duration from the server (older API, or a stage it has no measurement for):
        // fall back to the pre-2026-08-07 behaviour rather than guessing a duration. Half the
        // remaining band after ~2 minutes — slow enough that a fast stage barely moves.
        : 1 - Math.pow(0.5, stageElapsed / 120);
    pct = Math.min(ceiling - 1, Math.round(serverPct + (ceiling - serverPct) * eased));
  }
  if (pct != null) {
    pct = Math.max(pct, shownRef.current);          // monotonic: a bar that goes back reads as a fault
    shownRef.current = pct;
  }
  const lines = STAGE_ACTIVITY[job.stage] ?? FALLBACK_ACTIVITY;
  const activity = lines[tick % lines.length];

  return (
    <section
      aria-labelledby="vb-build-heading"
      style={{ marginTop: 20, background: colors.bgSurface, border: `1px solid ${colors.border}`, borderRadius: 12, padding: 22 }}
    >
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12 }}>
        <h2 id="vb-build-heading" style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
          {stageLabel(job.stage)}
        </h2>
        {pct != null && (
          <span style={{ fontFamily: "monospace", fontSize: 22, fontWeight: 700, color: colors.accent, lineHeight: 1 }}>
            {pct}%
          </span>
        )}
      </div>

      {/* The bar. `aria-valuenow` carries the same number the sighted user reads, so a screen-reader
          user is not left with a decorative div. */}
      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct ?? undefined}
        aria-label="Report build progress"
        style={{ marginTop: 12, height: 8, borderRadius: 999, background: colors.neutralChip, overflow: "hidden" }}
      >
        <div
          style={{
            width: `${pct ?? 6}%`,
            height: "100%",
            borderRadius: 999,
            background: colors.accent,
            // Eased, not snapped: the bar moves on each poll and a hard jump reads as a glitch.
            transition: "width 900ms cubic-bezier(.4,0,.2,1)",
          }}
        />
      </div>

      {/* The rotating activity line, tied to the real stage. `aria-live="polite"` so it is announced
          on change without interrupting, and it never speaks over the stage heading. */}
      <p
        role="status"
        aria-live="polite"
        style={{ margin: "14px 0 0", fontSize: 14, color: colors.textPrimary, minHeight: 40, lineHeight: 1.45 }}
      >
        {activity}
      </p>

      {job.ocr?.page != null && job.ocr?.pages != null && (
        <p style={{ margin: "6px 0 0", fontSize: 12, color: colors.textSecondary, fontFamily: "monospace" }}>
          Page {job.ocr.page} of {job.ocr.pages}
          {job.ocr.file ? ` — ${job.ocr.file}` : ""}
        </p>
      )}

      <div style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", fontSize: 12, color: colors.textSecondary }}>
        <span style={{ fontFamily: "monospace" }}>{formatElapsed(elapsed)}</span>
        <span aria-hidden="true">·</span>
        <span>You can leave this page open — it keeps working.</span>
      </div>

      {/* Set expectations before the wait feels wrong, not after.

          THIS NOTE USED TO BE FALSE (fixed 2026-08-07). It read "Building the PDF and Word files
          can take a couple of minutes on a long brief" — but the job never builds a PDF or a Word
          file. Both are rendered on the download route when the user clicks Download, after the job
          is already `done`. What is actually left at this point in the bar is the last read-through
          of the brief and the figure audit that checks every number against its source. If the PDF
          wait is worth warning about, the warning belongs on the download button; do not put it
          back here.

          The duration named is the one the owner measured (fastest ~2m, typical ~2m15s, long
          ~2m30s), stated as a range rather than a countdown — the panel must not imply a deadline
          it cannot keep. */}
      {pct != null && pct >= 80 && (
        <p style={{ margin: "10px 0 0", fontSize: 12, color: colors.textSecondary }}>
          Nearly there — the brief is being read back and every figure checked against its source.
          Most reports finish around two and a half minutes.
        </p>
      )}

      {notice && <p style={{ margin: "10px 0 0", fontSize: 12, color: colors.textSecondary }}>{notice}</p>}

      {stalled && (
        <p role="alert" style={{ margin: "12px 0 0", fontSize: 13, color: colors.caution, lineHeight: 1.5 }}>
          No progress reported for a while. This page keeps checking — if nothing moves in the next
          minute, go back to your review and press Generate again, or send hello@valubrief.ai job{" "}
          {job.id}.
        </p>
      )}
    </section>
  );
}

/** Seconds under a minute — the window in which someone decides nothing is happening. */
export function formatElapsed(seconds: number): string {
  if (seconds < 60) return `${seconds}s elapsed`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s}s elapsed`;
}
